"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useEffect, useState } from "react";
import { ChevronDown, Menu as MenuIcon, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Drawer, DrawerClose, DrawerContent, DrawerHeader, DrawerTitle, DrawerTrigger } from "@/components/ui/drawer";
import { NavigationMenu, NavigationMenuContent, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger, navigationMenuTriggerStyle } from "@/components/ui/navigation-menu";
import { MenuProvider, useMenu, type MenuItem } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function DesktopMenuSubitems({
  items
}: {
  items: MenuItem[];
}) {
  return <ul className="grid w-56 gap-1 p-2 bg-popover text-popover-foreground rounded-xs border border-border/60 shadow-md">
      {items.map((subItem, idx) => <li key={subItem.id} data-index={idx} className="group/sub">
          <NavigationMenuLink asChild>
            <Link to={subItem.href || "#"} className="block rounded-xs px-3 py-2 text-xs font-medium tracking-wide uppercase text-foreground hover:bg-muted/80 hover:text-primary transition-colors">
              {subItem.label}
            </Link>
          </NavigationMenuLink>
          {subItem.children && subItem.children.length > 0 && <div className="pl-3 pt-1">
              <DesktopMenuSubitems items={subItem.children} />
            </div>}
        </li>)}
    </ul>;
}
function DesktopNavigationConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex items-center gap-6">
        <div className="h-4 w-16 bg-muted animate-pulse rounded-xs" />
        <div className="h-4 w-20 bg-muted animate-pulse rounded-xs" />
        <div className="h-4 w-16 bg-muted animate-pulse rounded-xs" />
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden lg:flex">
      <NavigationMenuList className="gap-2">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            {item.children && item.children.length > 0 ? <>
                <NavigationMenuTrigger className="bg-transparent text-foreground hover:text-primary hover:bg-muted/50 data-[state=open]:bg-muted/60 text-xs uppercase tracking-widest font-medium px-3 py-2 h-auto transition-colors">
                  {item.label}
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <DesktopMenuSubitems items={item.children} />
                </NavigationMenuContent>
              </> : <NavigationMenuLink asChild>
                <Link to={item.href || "#"} className={`${navigationMenuTriggerStyle()} bg-transparent text-foreground hover:text-primary hover:bg-muted/50 text-xs uppercase tracking-widest font-medium px-3 py-2 h-auto transition-colors`}>
                  {item.label}
                </Link>
              </NavigationMenuLink>}
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuTree({
  items,
  level = 0
}: {
  items: MenuItem[];
  level?: number;
}) {
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({});
  const toggleSubmenu = (id: string) => {
    setOpenItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  return <ul className={`flex flex-col gap-1 w-full ${level > 0 ? "pl-4 border-l border-border/40 mt-1" : ""}`}>
      {items.map((item, idx) => {
      const hasKids = Boolean(item.children && item.children.length > 0);
      const isOpen = openItems[item.id] ?? false;
      return <li key={item.id} data-index={idx} className="flex flex-col">
            <div className="flex items-center justify-between py-2 border-b border-border/20">
              <Link to={item.href || "#"} className="text-sm font-medium tracking-wide uppercase text-foreground hover:text-primary transition-colors">
                {item.label}
              </Link>
              {hasKids && <Button type="button" variant="ghost" size="icon" onClick={() => toggleSubmenu(item.id)} aria-label={`Toggle ${item.label} submenu`} className="p-1 h-auto w-auto text-muted-foreground hover:text-foreground transition-colors">
                  <ChevronDown className={`size-4 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
                </Button>}
            </div>
            {hasKids && isOpen && item.children && <MobileMenuTree items={item.children} level={level + 1} />}
          </li>;
    })}
    </ul>;
}
function MobileNavigationConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 p-4">
        <div className="h-6 w-32 bg-muted animate-pulse rounded-xs" />
        <div className="h-6 w-40 bg-muted animate-pulse rounded-xs" />
        <div className="h-6 w-28 bg-muted animate-pulse rounded-xs" />
      </div>;
  }
  return <nav className="flex flex-col gap-4 py-2">
      <MobileMenuTree items={menuItems} />
    </nav>;
}
export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 16);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Smooth scroll for anchor navigation
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section id="header" data-nav="light" className={`sticky top-0 z-50 w-full bg-background transition-[border-color,box-shadow] duration-300 ${scrolled ? "border-b border-border shadow-xs" : "border-b border-border/40"}`}>
      <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-20 items-center justify-between gap-4">
          {/* Brand Logo Wordmark */}
          <div className="flex items-center shrink-0">
            <Link to="/" className="inline-flex items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
              <WvcLogo className="h-10 w-auto" />
            </Link>
          </div>

          {/* Primary Navigation Bar (Desktop) */}
          <div className="hidden lg:flex items-center justify-center flex-1">
            <MenuProvider menu_id="16">
              <DesktopNavigationConsumer />
            </MenuProvider>
          </div>

          {/* Actions: Primary CTA & Mobile Hamburger */}
          <div className="flex items-center gap-3 sm:gap-4 shrink-0">
            <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-xs uppercase tracking-widest text-xs font-semibold px-5 py-2.5 h-auto transition-all shadow-xs">
              <Link to="/AboutDiscoveryConsultationPage">Request Consultation</Link>
            </Button>

            {/* Mobile Navigation Drawer */}
            <div className="lg:hidden">
              <SafeHydrate><Drawer open={drawerOpen} onOpenChange={setDrawerOpen} direction="right">
                <DrawerTrigger asChild>
                  <Button variant="outline" size="icon" aria-label="Open Navigation Menu" className="border-border text-foreground hover:bg-muted rounded-xs size-9">
                    <MenuIcon className="size-5" />
                  </Button>
                </DrawerTrigger>
                <DrawerContent className="p-6 bg-background text-foreground border-l border-border max-w-xs w-full">
                  <DrawerHeader className="p-0 pb-6 border-b border-border flex flex-row items-center justify-between text-left">
                    <DrawerTitle className="text-base font-serif font-medium tracking-normal text-foreground">
                      Navigation
                    </DrawerTitle>
                    <DrawerClose asChild>
                      <Button variant="ghost" size="icon" aria-label="Close Navigation Menu" className="rounded-xs size-8 text-foreground">
                        <X className="size-4" />
                      </Button>
                    </DrawerClose>
                  </DrawerHeader>

                  <div className="py-6 overflow-y-auto">
                    <MenuProvider menu_id="16">
                      <MobileNavigationConsumer />
                    </MenuProvider>
                  </div>

                  <div className="pt-4 border-t border-border mt-auto">
                    <Button asChild className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-xs uppercase tracking-widest text-xs font-semibold py-3 h-auto" onClick={() => setDrawerOpen(false)}>
                      <Link to="/AboutDiscoveryConsultationPage">Request Consultation</Link>
                    </Button>
                  </div>
                </DrawerContent>
              </Drawer></SafeHydrate>
            </div>
          </div>
        </div>
      </div>
    </section>;
}